import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  Inject,
  Input, NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MolecularFindingService } from './services/molecular-finding.service';
import { IImmunotherapyFinding } from './interfaces/immunotherapy-finding.interface';
import { Subject } from 'rxjs';
import { IImmunotherapyConfig } from './interfaces/immunotherapy-config.interface';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { FindingsDifferentCompareComponent } from './findings-different-compare/findings-different-compare.component';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { pluck, take } from 'rxjs/operators';
import { Resources } from '@ispm/workflow/constants/resources.enum';
import * as immutable from 'immutable';
import { TestService } from '../../services/test.service';

@Component({
  selector: 'gen-immunotherapy',
  templateUrl: './immunotherapy.component.html',
  styleUrls: ['./immunotherapy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('Immunotherapy')
export class ImmunotherapyComponent implements OnInit, OnDestroy, IWidgetItem {
  @Input()
  viewMode = false;
  private isAlive: boolean = true;

  @Input()
  set revisionId(id: string) {
    if (!id) {
      return;
    }

    this.revId = id;
  }

  get revisionId() {
    return this.revId;
  }

  @Input() testType?: string;

  @Input()
  set values(values: IImmunotherapyFinding) {
    this.originalValues = values;
    this.editedValues = <IImmunotherapyFinding> { };

    for (const key in values) {
      this.editedValues[key] = Object.assign({ }, values[key].edit ? values[key].edit : values[key]);
    }

    if (!(this.config && values)) {
      return;
    }

    this.setConfig();
  }

  get values() {
    return this.editedValues;
  }

  @Input() editAllowed: boolean = true;

  @Input()
  set config(config: IImmunotherapyConfig) {
    this.conf = config;
    this.setConfig();
    this._changeDetector.detectChanges();
  }

  get config() {
    return this.conf;
  }

  @Input() edit: boolean = false;

  @Input()
  set compareMode(active: boolean) {
    this.compareModeStatus = active;
    this.edit = active;
    this.viewMode = !active;
  }

  get compareMode() {
    return this.compareModeStatus;
  }

  @Input()
  set listenToEvents(listen: boolean){
    if (listen) {
      // instand this.element.nativeElement been used it switch to window.document.body for there the content
      // of the dropdown will be appended else it will not work
      window.document.body.removeEventListener('click', this.triggerChangeDetection, { capture: true });
      window.document.body.addEventListener('click', this.triggerChangeDetection, { passive: true, capture: true });
    } else {
      window.document.body.removeEventListener('click', this.triggerChangeDetection, { capture: true });
    }
  }


  /**
   * variables which use for update immunotherapy
   */
  @Input() activeRevisionId?: string;
  @Input() workflowId?: string;
  @Output() valueUpdate: EventEmitter<IImmunotherapyFinding> = new EventEmitter;

  set widgetConfig(widgetConfig: any) {
    Object.assign(this, widgetConfig.widgetPayload);
    this._workflowConfigService.getResourcesMap()
      .pipe(
        take(1),
        pluck(Resources.RequestedRevision)
      )
      .subscribe((rev: string) => {
        this.revisionId = rev;

        if (
          this._testService.testConfiguration.molecularFindings &&
          this._testService.testConfiguration.molecularFindings.showOnTherapyTab
        ) {
          this.config = this._testService.testConfiguration.requiredAnnotations.find(annotation => {
            return annotation.type && annotation.type.toLowerCase() === 'immunotherapy';
          });
          this._changeDetector.markForCheck();
        } else {
          this.config = null;
        }
      });
  }

  set widgetData(widgetData: any) {
    if (widgetData) {
      this.values = widgetData;
    }
  }

  public show: boolean = true;
  public payloads: any = { };
  public validationMap: immutable.Map<string, boolean> = immutable.Map();
  private conf: IImmunotherapyConfig;
  private revId: string;
  private originalValues: IImmunotherapyFinding = { };
  private editedValues: IImmunotherapyFinding;
  private destroyer: Subject<any> = new Subject();
  private compareModeStatus: boolean = false;
  widgetUpdateValueCallback: (value) => void;
  private triggerChangeDetection: () => void = undefined;

  constructor(
    private molecularFinding: MolecularFindingService,
    private modalLoader: DynamicModalLoaderService,
    private _workflowConfigService: WorkflowConfigService,
    private element: ElementRef,
    private _zone: NgZone,
    private _testService: TestService,
    private _changeDetector: ChangeDetectorRef
  ) {
    this.triggerChangeDetection = () => {
      this._zone.runOutsideAngular(() => {
        setTimeout(() => {
          if (this.isAlive) {
            this._changeDetector.markForCheck();
            this._changeDetector.detectChanges();
          }
        });
      });
    };
  }

  ngOnInit() {
    if (!this.values) {
      this.loadValues();
    }
  }

  public loadValues() {
    this.molecularFinding.get()
      .takeUntil(this.destroyer.asObservable())
      .filter(values => !!values)
      .take(1)
      .subscribe((values) => {
        this.values = values;
        this._changeDetector.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.isAlive = false;

    if (this.destroyer) {
      this.destroyer.next();
      this.destroyer.complete();
    }

    window.document.body.removeEventListener('click', this.triggerChangeDetection);
  }

  public setTooltipStatus(key: string, status: boolean) {
    if (!this.payloads[key]) {
      this.payloads[key] = { };
    }

    this.payloads[key].showTooltip = status;
  }

  public getText(key: string) {
    if (!(this.config && this.payloads && this.payloads[key])) {
      return;
    }
    else if (this.payloads[key].text) {
      return this.payloads[key].text;
    }

    let valueObject;
    const keyResource = this.conf.resources.find(v => v.key == 'PDL1_text');

    if (this.originalValues[key]) {
      valueObject = this.originalValues[key] && this.originalValues[key].edit || this.originalValues[key];
    } else if (keyResource && keyResource.fallbackValue && this.originalValues[keyResource.fallbackValue]) {
      valueObject = this.originalValues[keyResource.fallbackValue] &&
        this.originalValues[keyResource.fallbackValue].edit ||
        this.originalValues[keyResource.fallbackValue]
      ;
    } else {
      valueObject = { value: '', level: '' };
    }

    let text = '';

    if (valueObject.level) {
      text += `${valueObject.level}`;

      if (valueObject.value != null && valueObject.value !== '') {
        text += ' (';

        if (this.payloads[key].prefix) {
          text += `${this.payloads[key].prefix} `;
        }

        text += this.setValueText(valueObject.value, key);

        if (this.payloads[key].suffix) {
          text += ` ${this.payloads[key].suffix}`;
        }

        text += ')';
      }
    }
    else if (valueObject.value != null && valueObject.value !== '') {
      text += `${this.payloads[key].prefix} ${this.setValueText(valueObject.value, key)} ${this.payloads[key].suffix}`;
    }

    this.payloads[key].text = text;

    return text;
  }

  public cancelEdit() {
    this.edit = false;
    this.loadValues();
  }

  /**
   * update the immunotherapy value which stored on molecular finding resource
   * emit the change and update the local value
   */
  public updateValue() {
    let indication = true;
    this.validationMap.forEach(status => {
      indication = indication && status;
    });

    if (!indication) {
      return;
    }

    this.modalLoader.popupModal(PlaceholderModalComponent,
      new PlaceholderModalLoaderPayload(
        'Please verify all changes made into the parameters before you proceed',
        FindingsDifferentCompareComponent,
        'Save changes',
        'Cancel',
        {
          updated: this.values,
          original: this.originalValues,
          config: this.config
        }
      ), ModalTypes.ALERT)
      .map(response => response.payload.status)
      .filter((response: modalResponse) => response === modalResponse.approve)
      .flatMap((response: modalResponse) => {
        const update = { ...this.values};

        for (const key in this.payloads) {
          if (!this.payloads[key].editable) {
            delete update[key];
          }

          this.payloads[key].text = null;
        }

        return this.molecularFinding.update(update, this.testType);
      })
      .subscribe(res => {
        this.edit = false;
        this.valueUpdate.emit(res);
        this.values = res;
        this._changeDetector.detectChanges();
      });
  }

  /**
   * set the configuration for each of the values
   */
  private setConfig() {
    if (!this.values) {
      return;
    }

    const tempObj = { };
    this.config?.resources.forEach((resource) => {
      if (!resource) {
        return;
      }

      const keys = resource.value.split(',').map(key => {
        const arr = key.split('.');
        return arr[arr.length - 1];
      });

      for (let i = 0; i < keys.length; i++) {
        if (this.values[keys[i]]) {
          tempObj[keys[i]] = this.values[keys[i]];
        } else if (resource.fallbackValue && this.values[resource.fallbackValue]) {
          tempObj[resource.key] = this.values[resource.fallbackValue];
        } else {
          tempObj[keys[i]] = { };
        }

        this.payloads[keys[i]] = resource.payload[i];
      }
    });
    this.editedValues = tempObj;
  }

  private setValueText(value, key) {
    switch (this.payloads[key].type && this.payloads[key].type.toLowerCase()) {
      case 'percentage':
        value += '%';
        break;
      default:
        break;
    }

    return value;
  }

  private updateWidget(value) {
    if (Reflect.has(this, 'widgetUpdateValueCallback')) {
      this.widgetUpdateValueCallback(value);
    }
  }

  setEditStatus(status: boolean) {
    this.edit = status;
    this.triggerChangeDetection();
  }

  updateValidation(key: any, event: boolean) {
    this.validationMap = this.validationMap.set(key, event);
  }
}
